import React, { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { WHITE, BLACK, GRAY_300 } from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { SettingsIcon } from "../../assets/svg/SettingsIcon";
import FacilityIcon from "../../assets/svg/FacilityIcon";
import ACTIONS from "../../store/action";
import {
  selectProvidersAllFacilities,
  selectFacilityListFilters,
  selectCanEditFacilities,
  DEFAULT_FACILITY_LIST_FILTERS
} from "../../store/selector";
import {
  BreadcrumbSection,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbCurrent
} from "../../component/sharedComponents/settingsEntity/components";
import { useStandardizedTracking } from "../../utils/pendo/useStandardizedTracking";
import { FacilityList, FACILITY_SORT_FIELDS } from "./FacilityList";
import { FacilityColumnSelector, readHiddenColumns, writeHiddenColumns } from "./FacilityColumnSelector";
import { ExportButton } from "../../component/ExportButton";
import { useFacilityExport } from "./useFacilityExport";
import { FACILITY_EVENTS, FACILITY_FEATURE_GROUP, FACILITY_PAGE_KEY } from "./analytics";

const { Text } = Typography;

const SEARCH_TRACK_DEBOUNCE_MS = 500;

// Spacing follows Figma PageContent/Facilities (4036:28806): card padding 16, column gap 16.
const Container = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 16px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
`;

// Figma Breadcrumbs: 8px between crumbs, 4px icon↔label, 16px to the page header
const ListBreadcrumbSection = styled(BreadcrumbSection)`
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 16px;

  > div {
    gap: 4px;
  }
`;

const ContentHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px; /* Figma: 16px between description and search */
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px; /* Figma Title + Description gap */
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: "Open Sans";
  font-size: ${sizes.medium_xlarge};
  font-weight: ${fontWeights.bold};
  line-height: 1.5;
  color: ${BLACK};
`;

const PageDescription = styled(Text)`
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  line-height: 1.5;
  color: ${BLACK};
  display: block;
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px; /* Figma: 16px between search and table */
`;

// Figma Search: 263 wide, padding 10px 20px, 1px Grey-300 border, radius 10
const SearchInput = styled(Input)`
  width: 263px;
  border-radius: 10px;
  border-color: ${GRAY_300};

  /* radius lives on the affix wrapper only — a rounded inner input clips the caret/selection */
  .ant-input {
    border-radius: 0;
    font-size: 14px;
    line-height: 22px;
  }

  .ant-input::placeholder {
    font-size: inherit;
    line-height: inherit;
  }

  &.ant-input-affix-wrapper {
    border-radius: 10px;
    padding: 10px 20px;
  }

  &.ant-input-affix-wrapper:hover {
    border-color: ${BLACK} !important;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ExportSlot = styled.div`
  min-height: 40px;
`;

// PRD: name "contains", NPI "starts with"; input trimmed; case-insensitive.
export const filterFacilitiesBySearch = (facilities, rawSearchTerm) => {
  const term = (rawSearchTerm || "").trim().toLowerCase();
  if (!term) return facilities;
  return facilities.filter(
    (facility) =>
      (facility.name || "").toLowerCase().includes(term) ||
      String(facility.npi || "")
        .toLowerCase()
        .startsWith(term)
  );
};

export const sortFacilities = (facilities, sortColumn, sortOrder) => {
  const field = FACILITY_SORT_FIELDS[sortColumn];
  if (!field || !sortOrder) return facilities;
  const direction = sortOrder === "descend" ? -1 : 1;
  return [...facilities].sort(
    (a, b) =>
      direction * String(a[field] || "").localeCompare(String(b[field] || ""), undefined, { sensitivity: "base" })
  );
};

export const FACILITIES_EXPORT_VIEW = "facilities";

export const FacilityManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { track } = useStandardizedTracking();
  const { exportFacilities, isExporting } = useFacilityExport();

  const facilities = useSelector(selectProvidersAllFacilities);
  const filters = useSelector(selectFacilityListFilters);
  const canEdit = useSelector(selectCanEditFacilities);
  const { searchTerm, currentPage, sortColumn, sortOrder } = filters;

  const [hiddenColumns, setHiddenColumns] = useState(readHiddenColumns);
  const hasTrackedView = useRef(false);
  const filteredCountRef = useRef(0);

  const setFilters = (patch) => dispatch(ACTIONS.setFacilityListFilters(patch));

  // Refresh the facility list and reset filters every time the page is entered, and refetch when
  // the tab regains focus so facilities created elsewhere (e.g. Back Office) show up without a
  // manual reload.
  useEffect(() => {
    dispatch(ACTIONS.providersAction("facility"));
    dispatch(ACTIONS.setFacilityListFilters({ ...DEFAULT_FACILITY_LIST_FILTERS }));

    const refetchOnFocus = () => dispatch(ACTIONS.providersAction("facility"));
    window.addEventListener("focus", refetchOnFocus);
    return () => window.removeEventListener("focus", refetchOnFocus);
  }, [dispatch]);

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    track(FACILITY_EVENTS.PAGE_VIEWED, {
      featureGroup: FACILITY_FEATURE_GROUP,
      pageKey: FACILITY_PAGE_KEY,
      contentKey: "facilities_list",
      additionalInfo: { facility_count: facilities.length }
    });
  }, [facilities.length, track]);

  const filteredFacilities = useMemo(
    () => sortFacilities(filterFacilitiesBySearch(facilities, searchTerm), sortColumn, sortOrder),
    [facilities, searchTerm, sortColumn, sortOrder]
  );

  filteredCountRef.current = filteredFacilities.length;

  const debouncedTrackSearch = useMemo(
    () =>
      debounce((value) => {
        track(FACILITY_EVENTS.SEARCHED, {
          featureGroup: FACILITY_FEATURE_GROUP,
          pageKey: FACILITY_PAGE_KEY,
          contentKey: "search",
          additionalInfo: {
            query_length: value.trim().length,
            results_count: filteredCountRef.current
          }
        });
      }, SEARCH_TRACK_DEBOUNCE_MS),
    [track]
  );

  useEffect(() => () => debouncedTrackSearch.cancel(), [debouncedTrackSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters({ searchTerm: value, currentPage: 1 });
    if (!value.trim()) {
      debouncedTrackSearch.cancel();
      return;
    }
    debouncedTrackSearch(value);
  };

  const handleHiddenColumnsChange = (next) => {
    setHiddenColumns(next);
    writeHiddenColumns(next);
  };

  const handleSortChange = (column, order) => setFilters({ sortColumn: column, sortOrder: order, currentPage: 1 });

  const handleEdit = (facility) => {
    track(FACILITY_EVENTS.EDIT_STARTED, {
      featureGroup: FACILITY_FEATURE_GROUP,
      pageKey: FACILITY_PAGE_KEY,
      contentKey: "edit_icon",
      additionalInfo: { facility_id: facility.id, entry_point: "table_icon" }
    });
    history.push(`/facilities-settings/${facility.id}/edit`);
  };

  const handleViewDetails = (facility) => history.push(`/facilities-settings/${facility.id}`);

  const hasFacilities = facilities.length > 0;

  return (
    <Container id="settings-facilities-page">
      <HeaderSection>
        <ListBreadcrumbSection>
          <BreadcrumbItem id="Facilities_Breadcrumbs_Settings" onClick={() => history.push("/settings")}>
            <SettingsIcon />
            {t("facility_management.breadcrumb.settings")}
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbCurrent>
            <FacilityIcon style={{ width: 24, height: 24 }} />
            {t("facility_management.breadcrumb.facilities")}
          </BreadcrumbCurrent>
        </ListBreadcrumbSection>
      </HeaderSection>

      <ContentHeaderSection>
        <LeftColumn>
          <PageTitle>{t("facility_management.list.page_title")}</PageTitle>
          <PageDescription>{t("facility_management.list.page_description")}</PageDescription>
        </LeftColumn>
        <RightColumn>
          <ExportSlot>
            <ExportButton
              id="settings-facilities-export-btn"
              selectedView={FACILITIES_EXPORT_VIEW}
              recordCount={filteredFacilities.length}
              isLoading={isExporting}
              disabled={!hasFacilities}
              onExport={(fileType) => exportFacilities(filteredFacilities, fileType, hiddenColumns)}
            />
          </ExportSlot>
        </RightColumn>
      </ContentHeaderSection>

      <ToolbarRow>
        <SearchInput
          id="settings-facilities-search-input"
          aria-label={t("facility_management.list.search_placeholder")}
          placeholder={t("facility_management.list.search_placeholder")}
          suffix={<SearchOutlined />}
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={!hasFacilities}
          allowClear
        />
        <FacilityColumnSelector hiddenColumns={hiddenColumns} onChange={handleHiddenColumnsChange} />
      </ToolbarRow>

      <ContentSection>
        <FacilityList
          facilities={filteredFacilities}
          isSearchResult={searchTerm.trim().length > 0}
          currentPage={currentPage}
          onPageChange={(page) => setFilters({ currentPage: page })}
          sortColumn={sortColumn === "name" ? "nameNpi" : sortColumn}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          hiddenColumns={hiddenColumns}
          canEdit={canEdit}
          onEdit={handleEdit}
          onViewDetails={handleViewDetails}
        />
      </ContentSection>
    </Container>
  );
};

export default FacilityManagement;
